import { useCallback, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import SVGAdd from 'visitor-ui-component-library-icons/icons/SVGAdd';
import VizExIcon from 'visitor-ui-component-library/icon/VizExIcon';
import I18n from 'I18n';
import SpotlightLauncherShell, { IconButton } from 'conversations-visitor-experience-components/visitor-widget/SpotlightLauncherShell';
import SpotlightAddMenu from 'conversations-visitor-experience-components/visitor-widget/SpotlightAddMenu';
import FileUploadsList from '../../attachments/components/FileUploadsList';
import SpotlightLauncherDragDropWrapper from './SpotlightLauncherDragDropWrapper';
import ScreenCaptureItem from './ScreenCaptureItem';
import AttachFileItem from './AttachFileItem';
// @ts-expect-error not typed
import { getSelectedThread } from '../../selected-thread/selectors/getSelectedThread';
import { getThreadsAsyncData } from '../../threads/selectors/getThreadsAsyncData';
import { isSucceeded } from '../../constants/asyncStatuses';
// @ts-expect-error not typed
import { getFileUploadListForCurrentThreadId } from '../../file-uploads/selectors/getFileUploadListForCurrentThreadId';
// @ts-expect-error not typed
import { removeAttachmentFromThread } from '../../file-uploads/actions/removeAttachmentFromThread';
import { isUploadCompleted } from 'conversations-internal-schema/file-upload/operators/isUploadCompleted';
import { useAppDispatch } from '../../buildStore';
import { publishMessageFromSpotlightLauncher } from '../../actions/publishMessageFromSpotlightLauncher';
import { useConsentPromptDataV2 } from '../../hooks/useConsentPromptDataV2';
import { isPersistedThread } from '../../threads/operators/isPersistedThread';
import { handleSpotlightMenuResize } from '../../post-message/handleSpotlightMenuResize';
import { canUploadAttachments as canUploadAttachmentsSelector } from '../../attachments/selectors/canUploadAttachments';
import { dropAttachmentFile } from '../../file-uploads/actions/dropAttachmentFile';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const SpotlightLauncher = ({
  onOpen,
  open = false
}) => {
  const dispatch = useAppDispatch();
  const selectedThread = useSelector(getSelectedThread);
  const threadsAsyncData = useSelector(getThreadsAsyncData);
  const areThreadsLoaded = isSucceeded(threadsAsyncData);
  const hasExistingThread = isPersistedThread(selectedThread);
  const canUpload = useSelector(state => selectedThread ? canUploadAttachmentsSelector(state, {
    thread: selectedThread
  }) : false);
  const {
    consentRequired: requiresConsent,
    showConsentButton: requiresExplicitConsent
  } = useConsentPromptDataV2();
  const stagedAttachments = useSelector(getFileUploadListForCurrentThreadId);
  const menuOpenExpandedRef = useRef(false);
  const isInputExpandedRef = useRef(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const hasUploadInProgress = (stagedAttachments === null || stagedAttachments === void 0 ? void 0 : stagedAttachments.size) > 0 && !stagedAttachments.every(isUploadCompleted);
  const areAttachActionsDisabled = !hasExistingThread || !canUpload;
  const onDropFiles = useCallback(files => {
    if (!selectedThread) return;
    dispatch(dropAttachmentFile(files, selectedThread));
  }, [dispatch, selectedThread]);
  const onSubmitMessage = useCallback(text => {
    dispatch(publishMessageFromSpotlightLauncher(text));
  }, [dispatch]);
  const onRemoveAttachment = useCallback(attachment => {
    if (!selectedThread) return;
    dispatch(removeAttachmentFromThread({
      attachment,
      thread: selectedThread
    }));
  }, [dispatch, selectedThread]);
  const closeMenu = useCallback(() => {
    menuOpenExpandedRef.current = false;
    setMenuOpen(false);
    handleSpotlightMenuResize(false);
  }, []);
  const openMenu = useCallback(() => {
    setMenuOpen(true);
    handleSpotlightMenuResize(true);
  }, []);
  const handleAddButtonClick = useCallback(e => {
    e.stopPropagation();
    if (menuOpen) {
      closeMenu();
    } else {
      openMenu();
    }
  }, [menuOpen, closeMenu, openMenu]);
  const handleExpandedChange = useCallback(expanded => {
    isInputExpandedRef.current = expanded;
    if (!expanded) menuOpenExpandedRef.current = false;
  }, []);
  const placeholder = I18n.text('conversations-visitor-experience-components.spotlightLauncher.placeholder');
  const sendAriaLabel = I18n.text('conversations-visitor-experience-components.spotlightLauncher.sendMessage');
  const moreOptionsAriaLabel = I18n.text('conversations-visitor-experience-components.spotlightLauncher.moreOptions');
  const buttonLabel = hasExistingThread ? I18n.text('conversations-visitor-experience-components.spotlightLauncher.continueConversation') : I18n.text('conversations-visitor-experience-components.spotlightLauncher.placeholder');
  if (!open && !areThreadsLoaded) return null;
  const isButton = !open && (hasExistingThread || requiresConsent);
  const fileUploads = stagedAttachments !== null && stagedAttachments !== void 0 && stagedAttachments.size ? /*#__PURE__*/_jsx(FileUploadsList, {
    stagedAttachments: stagedAttachments,
    onRemoveAttachment: onRemoveAttachment
  }) : null;
  const addMenu = /*#__PURE__*/_jsxs(SpotlightAddMenu, {
    trigger: /*#__PURE__*/_jsx(IconButton, {
      onPointerDown: e => e.stopPropagation(),
      onMouseDown: e => {
        e.preventDefault();
        menuOpenExpandedRef.current = open || isInputExpandedRef.current;
      },
      onClick: handleAddButtonClick,
      "aria-label": moreOptionsAriaLabel,
      "aria-haspopup": "menu",
      "aria-expanded": menuOpen,
      "data-test-id": "add-icon",
      disabled: requiresExplicitConsent,
      children: /*#__PURE__*/_jsx(VizExIcon, {
        size: "14px",
        icon: /*#__PURE__*/_jsx(SVGAdd, {})
      })
    }),
    isOpen: menuOpen,
    onClose: closeMenu,
    children: [/*#__PURE__*/_jsx(ScreenCaptureItem, {
      onClose: closeMenu,
      disabled: areAttachActionsDisabled
    }), /*#__PURE__*/_jsx(AttachFileItem, {
      onClose: closeMenu,
      disabled: areAttachActionsDisabled
    })]
  });
  return /*#__PURE__*/_jsx(SpotlightLauncherDragDropWrapper, {
    onDropFiles: onDropFiles,
    disabled: areAttachActionsDisabled,
    children: /*#__PURE__*/_jsx(SpotlightLauncherShell, {
      placeholder: placeholder,
      sendAriaLabel: sendAriaLabel,
      onSubmit: onSubmitMessage,
      onOpen: onOpen,
      open: open,
      disabled: requiresExplicitConsent,
      disableSubmit: hasUploadInProgress,
      hasAttachments: Boolean(stagedAttachments === null || stagedAttachments === void 0 ? void 0 : stagedAttachments.size),
      expanded: menuOpen && menuOpenExpandedRef.current,
      shouldAnimate: true,
      isButton: isButton,
      buttonLabel: buttonLabel,
      addMenu: addMenu,
      fileUploads: fileUploads,
      onExpandedChange: handleExpandedChange
    })
  });
};
SpotlightLauncher.displayName = 'SpotlightLauncher';
export default SpotlightLauncher;